import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../../../i18n";
// `header` namespace carries the "Change language" tooltip — see en/header.json.

// =============================================================================
// LanguageSwitcher
//
// A simple dropdown rendered in the header (as an <li>) OR anywhere else
// (as a <div>) via the `as` prop. Pick any of the supported languages; the
// choice persists to localStorage (handled by i18next's language detector)
// and re-renders every component using useTranslation.
// =============================================================================
// Flag emoji don't render on Windows Chrome — use flagcdn.com images instead.
const FlagImg = ({ flagCode, label }: { flagCode: string; label: string }) => (
  <img
    src={`https://flagcdn.com/24x18/${flagCode}.png`}
    srcSet={`https://flagcdn.com/48x36/${flagCode}.png 2x`}
    width={24}
    height={18}
    alt={label}
    style={{ borderRadius: 2, objectFit: "cover", flexShrink: 0 }}
  />
);

interface LanguageSwitcherProps {
  // When rendered inside a <ul> (e.g. the header nav) use "li".
  // When rendered anywhere else (e.g. login page, standalone) use "div".
  as?: "li" | "div";
  // Optional extra className + inline style on the outer wrapper,
  // mostly useful when positioning absolutely on a page like login.
  className?: string;
  style?: React.CSSProperties;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  as = "li",
  className,
  style,
}) => {
  const { t, i18n } = useTranslation("header");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);

  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) ||
    SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language?.split("-")[0]) ||
    SUPPORTED_LANGUAGES[0];

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const change = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  // Inner content is identical regardless of which outer element we render.
  const inner = (
    <>
      <button
        type="button"
        className={as === "li" ? "nav-link" : undefined}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
        title={t("actions.changeLanguage")}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: as === "li" ? undefined : "6px 12px",
          borderRadius: as === "li" ? undefined : 10,
          fontSize: 13,
          fontWeight: 600,
          color: "#1e293b",
          background: as === "li" ? "transparent" : "rgba(255,255,255,0.95)",
          border: as === "li" ? "none" : "1px solid #e2e8f0",
          cursor: "pointer",
          boxShadow: as === "li" ? undefined : "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <FlagImg flagCode={current.flagCode} label={current.label} />
        <span style={{ textTransform: "uppercase", letterSpacing: 0.5 }}>{current.code}</span>
        <i className="ti ti-chevron-down" style={{ fontSize: 14, color: "#64748b" }} />
      </button>
      <div
        className={`dropdown-menu ${open ? "show" : ""}`}
        style={{
          // Explicit positioning so the menu right-aligns to the button
          // regardless of Bootstrap's Popper being initialised or not.
          position: "absolute",
          top: "100%",
          right: 0,
          left: "auto",
          marginTop: 8,
          minWidth: 200,
          borderRadius: 12,
          border: "1px solid #e2e8f0",
          boxShadow: "0 12px 36px rgba(0,0,0,0.12)",
          padding: 6,
          overflow: "hidden",
          zIndex: 1050,
        }}
      >
        {SUPPORTED_LANGUAGES.map((lang) => {
          const active = lang.code === current.code;
          return (
            <button
              key={lang.code}
              type="button"
              className="dropdown-item"
              onClick={() => change(lang.code)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                background: active ? "#fff7ed" : "transparent",
                color: active ? "#e85b28" : "#1e293b",
                cursor: "pointer",
                border: "none",
                width: "100%",
                textAlign: "left",
              }}
            >
              <FlagImg flagCode={lang.flagCode} label={lang.label} />
              <span style={{ flex: 1 }}>{lang.label}</span>
              <span style={{ fontSize: 11, color: active ? "#e85b28" : "#94a3b8", textTransform: "uppercase" }}>
                {lang.code}
              </span>
              {active && <i className="ti ti-check" style={{ color: "#e85b28" }} />}
            </button>
          );
        })}
      </div>
    </>
  );

  if (as === "div") {
    return (
      <div
        ref={containerRef as React.RefObject<HTMLDivElement>}
        className={`dropdown ${className || ""}`}
        style={{ position: "relative", display: "inline-block", ...style }}
      >
        {inner}
      </div>
    );
  }

  return (
    <li
      ref={containerRef as React.RefObject<HTMLLIElement>}
      className={`nav-item dropdown nav-item-box ${className || ""}`}
      style={style}
    >
      {inner}
    </li>
  );
};

export default LanguageSwitcher;
